import { useEffect, useState } from "react";
import { pick } from "../i18n/index.js";
import { navigate } from "../router.js";
import { battlesApi } from "../units/api.js";
import type { BattleListEntry, BattleReport } from "../units/types.js";
import { GameIcon } from "../assets/gameIcons.js";
import { Button } from "../ui/Button.js";
import { LootRow } from "./Results.js";
import { GrowthPanel, RepairPanel } from "./Aftermath.js";
import type { BattleInput } from "@zend/rules";
import { battleConclusion } from "./TacticsPanel.js";

export function BattleJournal({ reportId }: { reportId?: string | undefined }) {
  const [rows, setRows] = useState<BattleListEntry[]>([]),
    [report, setReport] = useState<BattleReport | null>(null),
    [error, setError] = useState(false),
    [revision, setRevision] = useState(0),
    [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setError(false);
    setLoading(true);
    void battlesApi.list().then(
      (r) => {
        if (alive) {
          setRows(r);
          setLoading(false);
        }
      },
      () => {
        if (alive) {
          setError(true);
          setLoading(false);
        }
      },
    );
    return () => {
      alive = false;
    };
  }, [revision]);
  const selected = reportId || rows[0]?.id;
  useEffect(() => {
    let alive = true;
    setReport(null);
    if (selected)
      void battlesApi.report(selected).then(
        (r) => {
          if (alive) setReport(r);
        },
        () => {
          if (alive) setError(true);
        },
      );
    return () => {
      alive = false;
    };
  }, [selected, revision]);
  return (
    <div className="page battle-journal" data-testid="battle-journal">
      <header>
        <div>
          <small>{pick({ de: "DEIN EINSATZARCHIV", en: "YOUR OPERATION ARCHIVE" })}</small>
          <h1>{pick({ de: "Kampfberichte", en: "Battle reports" })}</h1>

        </div>
        <Button onClick={() => setRevision((n) => n + 1)}>
          {pick({ de: "Aktualisieren", en: "Refresh" })}
        </Button>
      </header>
      {error ? (
        <p role="alert">
          {pick({
            de: "Bericht konnte nicht geladen werden. Bitte erneut aktualisieren.",
            en: "Could not load the report. Please refresh.",
          })}
        </p>
      ) : null}
      {!rows.length && !error && !loading ? (
        <section className="operation-empty">
          <GameIcon id="zmb.shambler" size={96} />
          <h2>{pick({ de: "Dein erster Einsatz wartet", en: "Your first operation awaits" })}</h2>
          <p>
            {pick({
              de: "Suche ein Level-1-Rudel auf der Karte. Vor dem Angriff siehst du Gegner, Trupp und Beute. Dein Bericht bleibt danach hier erhalten.",
              en: "Find a level-1 pack on the map. Review enemies, squad and loot before attacking. Your report will stay here afterward.",
            })}
          </p>
          <Button variant="gold" onClick={() => navigate("/app/operations")}>
            {pick({ de: "Auftrag auswählen", en: "Choose an operation" })}
          </Button>
        </section>
      ) : null}
      <nav className="journal-list" aria-label={pick({ de: "Einsätze", en: "Operations" })}>
        {rows.map((row) => (
          <button
            key={row.id}
            aria-pressed={selected === row.id}
            onClick={() => navigate("/app/reports/" + row.id)}
          >
            <strong>
              {row.won ? pick({ de: "Sieg", en: "Victory" }) : pick({ de: "Niederlage", en: "Defeat" })} ·{" "}
              {row.opponentName ??
                (
                  {
                    pack: "Infiziertenrudel",
                    hive: "Hive",
                    boss: "Weltboss",
                    expedition: "Expedition",
                    nexus: "NEXUS",
                    arena: "Arena",
                    pvp: "Überfall",
                  } as Record<string, string>
                )[row.context] ??
                row.context}
            </strong>
            <small>
              {new Date(row.createdAt).toLocaleString()} · {(row.ticks / 10).toFixed(1)} s
            </small>
          </button>
        ))}
      </nav>
      {report ? (
        <article className="journal-report" data-testid="journal-report">
          <header>
            <h2>
              {report.won
                ? pick({ de: "Einsatz gewonnen", en: "Operation won" })
                : pick({ de: "Einsatz verloren", en: "Operation lost" })}
            </h2>
            <Button
              variant="gold"
              onClick={() => navigate("/app/battle/" + report.id)}
              data-testid="journal-watch"
            >
              {pick({ de: "Kampf ansehen", en: "Watch battle" })}
            </Button>
          </header>
          <h3>{pick({ de: "Beute", en: "Loot" })}</h3>
          <LootRow loot={report.loot} />
          <p className="hint">{battleConclusion(report.input as BattleInput, report.summary)}</p>
          {report.context==="pvp" ? <p className="hint">
            {pick({
              de: "Beim Überfall werden geraubte Rohstoffe erst nach Rückkehr ins Lager geliefert. Der Bericht zeigt das Kampfergebnis; der Marsch zeigt die Lieferung.",
              en: "Raided resources are delivered on return. This report shows the battle; the march shows delivery.",
            })}
          </p> : null}
          <GrowthPanel report={report} />
          <RepairPanel battleId={report.id} />
          <div className="operation-actions">
            <Button onClick={() => navigate("/app/units/squads")}>
              {pick({ de: "Trupps & Ersatzteam", en: "Squads & reserve team" })}
            </Button>
            <Button onClick={() => navigate("/app/operations")}>
              {pick({ de: "Nächster Auftrag", en: "Next operation" })}
            </Button>
          </div>
        </article>
      ) : null}
    </div>
  );
}
